<template>
    <v-container fluid class="pa-2 pa-md-4">
      <!-- VISTA 1: LISTADO GLOBAL -->
      <div v-if="!selectedTrabajo">
        <v-row align="center" class="mb-6 mt-2">
          <v-col cols="12" sm="auto">
            <h1 class="headline font-weight-bold mb-0 text-center text-sm-left">
              Trabajos
            </h1>
          </v-col>
        </v-row>
  
        <!-- FILTRO POR MÁQUINA -->
        <v-text-field
          v-model="searchQuery"
          prepend-inner-icon="mdi-magnify"
          label="Filtrar por nombre de máquina..."
          outlined
          dense
          hide-details
          clearable
          class="mb-6 rounded-xl white"
        ></v-text-field>
  
        <!-- SECCIÓN MANTENIMIENTO CORRECTIVO -->
        <div>
          <h2 class="subtitle-1 mb-3 error--text font-weight-bold d-flex align-center">
            <v-icon left small color="error">mdi-tools</v-icon>
            Mantenimiento Correctivo
          </h2>
  
          <v-card outlined class="rounded-xl">
            <v-list three-line class="pa-0">
              <template v-for="(item, index) in filteredCorrectivos">
                <v-list-item :key="'corr-' + index" class="py-3">
                  <v-list-item-content>
                    <div class="d-flex justify-space-between align-start mb-1">
                      <span class="caption font-weight-bold grey--text">{{ formatDate(item.fecha_programada) }}</span>
                      <v-chip :color="item.esIncompleto ? 'orange darken-2' : 'error'" x-small dark class="text-uppercase">
                        {{ item.esIncompleto ? 'En curso' : 'Pendiente' }}
                      </v-chip>
                    </div>
                    <v-list-item-title class="body-1 font-weight-bold">{{ item.maquina }}</v-list-item-title>
                    <v-list-item-subtitle class="mt-1 text-wrap">{{ item.descripcion }}</v-list-item-subtitle>
                  </v-list-item-content>
                  
                  <v-list-item-action>
                    <v-btn 
                      :color="item.esIncompleto ? 'orange darken-2' : 'error'" 
                      icon 
                      outlined 
                      small 
                      @click="iniciarTrabajo(item, 'correctivo')"
                    >
                      <v-icon>mdi-chevron-right</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
                <v-divider v-if="index < filteredCorrectivos.length - 1" :key="'div-c-'+index"></v-divider>
              </template>
              <v-list-item v-if="filteredCorrectivos.length === 0" class="text-center py-4 grey--text">
                No se encontraron correctivos
              </v-list-item>
            </v-list>
          </v-card>
        </div>
        
        <!-- SECCIÓN MANTENIMIENTO PREVENTIVO -->
        <div class="mt-8 mb-8">
          <h2 class="subtitle-1 mb-3 primary--text font-weight-bold d-flex align-center">
            <v-icon left small color="primary">mdi-calendar-check</v-icon>
            Mantenimiento Preventivo
          </h2>
  
          <v-card outlined class="rounded-xl">
            <v-list three-line class="pa-0">
              <template v-for="(item, index) in filteredPreventivos">
                <v-list-item :key="'prev-' + index" class="py-3">
                  <v-list-item-content>
                    <div class="d-flex justify-space-between align-start mb-1">
                      <span class="caption font-weight-bold grey--text">{{ formatDate(item.fecha_programada) }}</span>
                      <v-chip :color="item.esIncompleto ? 'warning' : 'info'" x-small dark class="text-uppercase">
                        {{ item.esIncompleto ? 'En curso' : 'Sin empezar' }}
                      </v-chip>
                    </div>
                    <v-list-item-title class="body-1 font-weight-bold">{{ item.maquina }}</v-list-item-title>
                    <v-list-item-subtitle class="mt-1">{{ item.plantilla }}</v-list-item-subtitle>
                  </v-list-item-content>
  
                  <v-list-item-action>
                    <v-btn 
                      :color="item.esIncompleto ? 'warning' : 'primary'" 
                      icon 
                      outlined 
                      small 
                      @click="iniciarTrabajo(item, 'preventivo')"
                    >
                      <v-icon>mdi-chevron-right</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
                <v-divider v-if="index < filteredPreventivos.length - 1" :key="'div-p-'+index"></v-divider>
              </template>
              <v-list-item v-if="filteredPreventivos.length === 0" class="text-center py-4 grey--text">
                No se encontraron preventivos
              </v-list-item>
            </v-list>
          </v-card>
        </div>
      </div>
  
      <!-- VISTA 2: EJECUCIÓN DE TRABAJO -->
      <div v-else>
        <v-btn text class="mb-4" @click="selectedTrabajo = null; loadAll()">
          <v-icon left>mdi-arrow-left</v-icon>
          Volver al listado
        </v-btn>
  
        <!-- CABECERA DE EJECUCIÓN -->
        <v-card outlined class="rounded-xl pa-6 mb-6" :class="tipoTrabajo === 'preventivo' ? 'blue lighten-5' : 'red lighten-5'">
          <div class="d-flex justify-space-between align-start">
              <div>
                  <div class="overline font-weight-bold" :class="tipoTrabajo === 'preventivo' ? 'primary--text' : 'error--text'">
                      {{ tipoTrabajo === 'preventivo' ? selectedTrabajo.plantilla : 'TRABAJO CORRECTIVO' }}
                  </div>
                  <h2 class="display-1 font-weight-bold">{{ selectedTrabajo.maquina }}</h2>
                  <div v-if="tipoTrabajo === 'correctivo'" class="subtitle-1 mt-2"><b>Incidencia:</b> {{ selectedTrabajo.descripcion }}</div>
              </div>
              <v-chip :color="tipoTrabajo === 'preventivo' ? 'primary' : 'error'" dark>
                  {{ tipoTrabajo === 'preventivo' ? selectedTrabajo.frecuencia : 'CORRECTIVO' }}
              </v-chip>
          </div>
        </v-card>
  
        <!-- LISTA DE TAREAS -->
        <v-card outlined class="rounded-xl">
          <v-card-title class="pa-4 font-weight-bold">Tareas a realizar</v-card-title>
          <v-divider></v-divider>
          
          <v-list class="pa-0">
            <template v-for="(tarea, index) in selectedTrabajo.tareas">
              <v-list-item :key="index" class="py-4 px-6">
                <v-list-item-content>
                  <v-list-item-title class="body-1 font-weight-medium mb-1">
                    {{ tarea.descripcion }}
                  </v-list-item-title>
                  <v-list-item-subtitle v-if="tipoTrabajo === 'preventivo' && tarea.estado === 'mal' && tarea.correctivo" class="error--text font-italic">
                    <v-icon x-small color="error">mdi-tools</v-icon> Correctivo: {{ tarea.correctivo.descripcion }}
                    <v-btn text x-small color="primary" @click="abrirModalCorrectivo(index)">Editar</v-btn>
                  </v-list-item-subtitle>
                </v-list-item-content>
  
                <v-list-item-action>
                  <!-- CASE 1: PREVENTIVO -->
                  <v-btn-toggle v-if="tipoTrabajo === 'preventivo'" v-model="tarea.estado" dense @change="handleEstadoChange($event, index)">
                    <v-btn value="bien" color="success" outlined small>BIEN</v-btn>
                    <v-btn value="mal" color="error" outlined small>MAL</v-btn>
                    <v-btn :value="null" color="grey" outlined small>PTE</v-btn>
                  </v-btn-toggle>
  
                  <!-- CASE 2: CORRECTIVO -->
                  <v-checkbox v-else v-model="tarea.estado" color="success" hide-details class="mt-0"></v-checkbox>
                </v-list-item-action>
              </v-list-item>
              <v-divider v-if="index < selectedTrabajo.tareas.length - 1" :key="'div-t-'+index"></v-divider>
            </template>
          </v-list>
        </v-card>
  
        <!-- DETALLES REPARACIÓN (Solo correctivo) -->
        <v-card v-if="tipoTrabajo === 'correctivo'" outlined class="rounded-xl mt-6 pa-6">
          <v-card-title class="pa-0 mb-4 font-weight-bold error--text">Detalles de la Reparación</v-card-title>
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field v-model="selectedTrabajo.fecha_programada" label="Fecha Programada" type="date" outlined dense></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="selectedTrabajo.empresa"
                :items="clientes"
                item-text="nombre"
                item-value="nombre" 
                label="Empresa"
                outlined
                dense
                :loading="loadingClientes"
                clearable
              ></v-select>
            </v-col>
            <v-col cols="6" sm="4">
              <v-text-field v-model.number="selectedTrabajo.tiempo_min" label="Tiempo (min)" type="number" outlined dense></v-text-field>
            </v-col>
            <v-col cols="6" sm="4">
              <v-text-field v-model.number="selectedTrabajo.coste" label="Coste (€)" type="number" outlined dense></v-text-field>
            </v-col>
            <v-col cols="12" sm="4">
              <v-switch v-model="selectedTrabajo.parada_produccion" label="Parada Producción" color="error" hide-details dense></v-switch>
            </v-col>
          </v-row>
        </v-card>
  
        <!-- BOTONES ACCIÓN FINAL -->
        <v-row class="mt-8 px-2">
          <v-spacer></v-spacer>
          <v-btn 
            color="success" 
            large 
            min-width="200" 
            depressed
            :loading="isSaving" 
            @click="tipoTrabajo === 'preventivo' ? enviarPreventivo() : enviarCorrectivo()"
          >
            <v-icon left>mdi-content-save-check</v-icon>
            Finalizar {{ tipoTrabajo === 'preventivo' ? 'Preventivo' : 'Reparación' }}
          </v-btn>
        </v-row>
      </div>
  
      <!-- MODAL GENERAR CORRECTIVO -->
      <v-dialog v-model="correctivoDialog" max-width="700px" persistent>
        <v-card class="rounded-xl">
          <v-toolbar color="error" dark flat dense>
            <v-toolbar-title>Programar Acción Correctiva</v-toolbar-title>
          </v-toolbar>
          <v-card-text class="pa-6">
            <v-textarea v-model="correctivoForm.descripcion" label="¿Qué se ha encontrado?" outlined rows="2" class="mb-4"></v-textarea>
            
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field v-model="correctivoForm.fecha_programada" label="Fecha Programada" type="date" outlined dense></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-autocomplete
                  v-model="correctivoForm.ids_operarios"
                  :items="filteredOperarios"
                  item-text="username"
                  item-value="id"
                  label="Operarios responsables de la reparación"
                  multiple
                  chips
                  outlined
                  dense
                  prepend-inner-icon="mdi-account-multiple"
                  :rules="[v => (v && v.length > 0) || 'Selecciona al menos un operario']"
                >
                  <!-- Slot opcional para ver mejor los nombres en los chips -->
                  <template v-slot:selection="data">
                    <v-chip
                      v-bind="data.attrs"
                      :input-value="data.selected"
                      close
                      x-small
                      @click:close="correctivoForm.ids_operarios.splice(correctivoForm.ids_operarios.indexOf(data.item.id), 1)"
                    >
                      {{ data.item.username }}
                    </v-chip>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="correctivoForm.empresa"
                  :items="clientes"
                  item-text="nombre"
                  item-value="nombre"
                  label="Subcontratación"
                  outlined
                  dense
                  :loading="loadingClientes"
                  clearable
                ></v-select>
              </v-col>
              <v-col cols="6" sm="4">
                <v-text-field v-model.number="correctivoForm.tiempo_min" label="Estimar min." type="number" outlined dense></v-text-field>
              </v-col>
              <v-col cols="6" sm="4">
                <v-text-field v-model.number="correctivoForm.coste" label="Estimar coste" type="number" outlined dense suffix="€"></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-switch v-model="correctivoForm.parada_produccion" label="¿Parada prod?" color="error" hide-details dense></v-switch>
              </v-col>
            </v-row>
  
            <v-divider class="my-4"></v-divider>
  
            <div class="d-flex justify-space-between align-center mb-2">
              <span class="font-weight-bold">Tareas de reparación:</span>
              <v-btn x-small color="primary" @click="correctivoForm.tareas_correctivas.push('')">Añadir Tarea</v-btn>
            </div>
            <div v-for="(t, i) in correctivoForm.tareas_correctivas" :key="i" class="d-flex align-center mb-2">
              <v-text-field v-model="correctivoForm.tareas_correctivas[i]" dense placeholder="Tarea..."></v-text-field>
              <v-btn icon small @click="correctivoForm.tareas_correctivas.splice(i,1)">
                <v-icon color="grey">mdi-delete</v-icon>
              </v-btn>
            </div>
          </v-card-text>
          <v-card-actions class="pa-6">
            <v-spacer></v-spacer>
            <v-btn text @click="cancelarCorrectivo">Cancelar</v-btn>
            <v-btn 
              color="error" 
              depressed 
              :disabled="!isCorrectivoFormValido" 
              @click="guardarCorrectivoLocal"
            >
              Guardar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </template>
  
  <script>
  import axios from 'axios'
  import { jwtDecode } from "jwt-decode";
  import { mapActions } from 'vuex';
  
  export default {
    data() {
      return {
        searchQuery: '',
        trabajosAbiertos: [],
        trabajosIncompletos: [],
        correctivosAbiertos: [],
        correctivosIncompletos: [],
        selectedTrabajo: null,
        tipoTrabajo: null,
        isSaving: false,
        correctivoDialog: false,
        currentTareaIndex: null,
        correctivoForm: {
          descripcion: '',
          tareas_correctivas: [],
          fecha_programada: '',
          empresa: '',
          tiempo_min: 0,
          coste: 0,
          parada_produccion: false,
          ids_operarios: [],
        },
        advanceDialog: false,
        selectedPlantillaId: null,
        plantillasActivas: [],
        loadingPlantillas: false,
        isAdvancing: false,
        clientes: [],
        loadingClientes: false,
        refreshInterval: null,

        operarios: [],
      }
    },
    computed: {
      idEmpresa() {
        return localStorage.getItem("clienteSeleccionado");
      },
      filteredPreventivos() {
        const query = this.searchQuery ? this.searchQuery.toLowerCase().trim() : ''
        const list = [
          ...this.trabajosIncompletos.map(t => ({ ...t, esIncompleto: true })),
          ...this.trabajosAbiertos.map(t => ({ ...t, esIncompleto: false }))
        ]
        if (!query) return list
        return list.filter(item => item.maquina && item.maquina.toLowerCase().includes(query))
      },
      filteredCorrectivos() {
        const query = this.searchQuery ? this.searchQuery.toLowerCase().trim() : ''
        const list = [
          ...this.correctivosIncompletos.map(c => ({ ...c, esIncompleto: true })),
          ...this.correctivosAbiertos.map(c => ({ ...c, esIncompleto: false }))
        ]
        if (!query) return list
        return list.filter(item => item.maquina && item.maquina.toLowerCase().includes(query))
      },
      allPreventivos() {
        return [...this.filteredPreventivos]
      },
      isCorrectivoFormValido() {
        const tieneDescripcion = !!this.correctivoForm.descripcion && this.correctivoForm.descripcion.trim().length > 0;
        const tieneTareasValidas = this.correctivoForm.tareas_correctivas.some(t => t && t.trim().length > 0);
        const operariosAsignados = this.correctivoForm.ids_operarios.length > 0;
        return tieneDescripcion && tieneTareasValidas && operariosAsignados;
      },
      filteredOperarios() {
        // 1. Verificamos que haya un trabajo seleccionado
        if (!this.selectedTrabajo) return [];

        // 2. Verificamos que tenga la propiedad ids_operarios y que sea un array
        const ids = this.selectedTrabajo.ids_operarios;
        if (!ids || !Array.isArray(ids)) return [];

        // 3. Ahora es seguro filtrar
        return this.operarios.filter((o) => ids.includes(o.id));
      },
    },
    mounted() {
      this.loadAll();
      this.loadUsuarios();
      this.loadClientes();
      this.refreshInterval = setInterval(() => {
        this.loadAll()
      }, 60000)
    },
    beforeDestroy() {
      if (this.refreshInterval) clearInterval(this.refreshInterval)
    },
    methods: {
      ...mapActions(["getData"]),
      async loadAll() {
        try {
          const id_operario = jwtDecode(localStorage.getItem("token")).id
          const [pa, pi, ca, ci] = await Promise.all([
            axios.get(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/trabajos/abiertos`, { 
              params: { id_operario: id_operario } 
            }),
            axios.get(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/trabajos/incompleto`, { 
              params: { id_operario: id_operario } 
            }),
            axios.get(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/correctivos/abiertos`, { 
              params: { id_operario: id_operario } 
            }),
            axios.get(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/correctivos/incompletos`, { 
              params: { id_operario: id_operario } 
            })
          ]);
          this.trabajosAbiertos = pa.data.trabajos
          this.trabajosIncompletos = pi.data.trabajos
          this.correctivosAbiertos = ca.data.correctivos || ca.data
          this.correctivosIncompletos = ci.data.correctivos || ci.data
        } catch (e) { console.error(e) }
      },

      async loadUsuarios() {
        // this.loading = true;
        try {
          const response = await this.getData({ path: "django/portal/get_all_users" });
          this.operarios = response.users.filter((c) => c.id_cliente == this.idEmpresa) || response;
        } catch (err) {
          console.error("Error al recoger usuarios");
        } finally {
          // this.loading = false;
        }
      },
      async loadClientes() {
        // this.loading = true;
        try {
          const response = await axios.get(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/clientes`, { 
              params: { id_empresa: this.idEmpresa } 
            })
          this.clientes = response.data.clientes;
        } catch (err) {
          console.error("Error al recoger clientes");
        } finally {
          // this.loading = false;
        }
      },

      iniciarTrabajo(item, tipo) {
        const copia = JSON.parse(JSON.stringify(item));
        
        // ASEGURAR SIEMPRE QUE EXISTA ids_operarios COMO ARRAY
        if (!copia.ids_operarios) {
          copia.ids_operarios = [];
        }

        if (tipo === 'correctivo') {
          copia.tareas = copia.tareas_correctivas || [];
          copia.tareas = copia.tareas.map(t => ({ ...t, estado: t.estado === true }));
          copia.fecha_programada = copia.fecha_programada ? copia.fecha_programada.split('T')[0] : '';
          copia.empresa = copia.empresa || '';
          copia.tiempo_min = copia.tiempo_min || 0;
          copia.coste = copia.coste || 0;
          copia.parada_produccion = !!copia.parada_produccion;
        }
        
        this.selectedTrabajo = copia;
        this.tipoTrabajo = tipo;
      },
      handleEstadoChange(valor, index) {
        if (valor === 'mal') this.abrirModalCorrectivo(index)
      },
      abrirModalCorrectivo(index) {
        this.currentTareaIndex = index;
        const existing = this.selectedTrabajo.tareas[index].correctivo;
        
        if (existing) {
          this.correctivoForm = JSON.parse(JSON.stringify(existing));
          // Refuerzo: si por algún motivo el correctivo guardado no tiene operarios
          if (!this.correctivoForm.ids_operarios) {
            this.$set(this.correctivoForm, 'ids_operarios', []);
          }
        } else {
          this.correctivoForm = {
            descripcion: '',
            tareas_correctivas: [''],
            fecha_programada: new Date().toISOString().split('T')[0],
            empresa: '',
            tiempo_min: 0,
            coste: 0,
            parada_produccion: false,
            ids_operarios: [], // Siempre inicializado
          };
        }
        this.correctivoDialog = true;
      },
      cancelarCorrectivo() {
        if (!this.selectedTrabajo.tareas[this.currentTareaIndex].correctivo) {
          this.selectedTrabajo.tareas[this.currentTareaIndex].estado = null
        }
        this.correctivoDialog = false
      },
      guardarCorrectivoLocal() {
        this.selectedTrabajo.tareas[this.currentTareaIndex].correctivo = { ...this.correctivoForm };
        this.selectedTrabajo.tareas[this.currentTareaIndex].correctivo.tareas_correctivas = 
          this.correctivoForm.tareas_correctivas.filter(t => t.trim() !== '');
        this.correctivoDialog = false
      },
      async enviarPreventivo() {
        this.isSaving = true
        try {
          await axios.post(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/trabajos/guardar`, {
            trabajo_id: this.selectedTrabajo.id,
            tareas: this.selectedTrabajo.tareas
          })
          const tareasConIncidencia = this.selectedTrabajo.tareas.filter(t => t.estado === 'mal' && t.correctivo)
          if (tareasConIncidencia.length > 0) {
            await Promise.all(tareasConIncidencia.map(tarea => {
              return axios.post(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/correctivos/guardar`, {
                id_trabajo: this.selectedTrabajo.id,
                id_trabajo_tarea: tarea.id,
                descripcion: tarea.correctivo.descripcion,
                tareas_correctivas: tarea.correctivo.tareas_correctivas,
                fecha_programada: tarea.correctivo.fecha_programada,
                empresa: tarea.correctivo.empresa,
                tiempo_min: tarea.correctivo.tiempo_min,
                coste: tarea.correctivo.coste,
                parada_produccion: tarea.correctivo.parada_produccion,
                ids_operarios: tarea.correctivo.ids_operarios
              })
            }))
          }
          this.selectedTrabajo = null; this.loadAll();
          this.$avisar('Preventivo guardado', "success");
        } catch (e) { this.$avisar('Error al guardar', "error"); } finally { this.isSaving = false; }
      },
      async enviarCorrectivo() {
        this.isSaving = true
        try {
          await axios.post(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/correctivos/actualizar`, {
            correctivo_id: this.selectedTrabajo.id,
            tareas: this.selectedTrabajo.tareas,
            fecha_programada: this.selectedTrabajo.fecha_programada,
            empresa: this.selectedTrabajo.empresa,
            tiempo_min: this.selectedTrabajo.tiempo_min,
            coste: this.selectedTrabajo.coste,
            parada_produccion: this.selectedTrabajo.parada_produccion
          })
          this.selectedTrabajo = null; this.loadAll();
          this.$avisar('Reparación finalizada', "success");
        } catch (e) { this.$avisar('Error al actualizar', "error"); } finally { this.isSaving = false; }
      },
      formatDate(d) { return d ? new Intl.DateTimeFormat('es-ES').format(new Date(d)) : '-' }
    }
  }
  </script>
  
  <style scoped>
  .rounded-xl { border-radius: 16px !important; }
  .text-wrap { white-space: normal !important; }
  </style>